import { createSelector } from '@reduxjs/toolkit';

import type { AppState } from '@/store/store.ts';
import { tabsAdapter } from '@/store/tabs/adapter.ts';
import { selectActiveTab, selectActiveTabId, selectTabsState } from '@/store/tabs/slice.ts';

export const selectTab = (state: AppState, tabId: string) => {
  return state.tabs.tabs.entities[tabId];
};

const tabsAdapterSelectors = tabsAdapter.getSelectors();

export const selectSelectedTabType = createSelector(selectActiveTab, (tab) => tab?.tabType);

const selectTabs = createSelector(selectTabsState, (state) => state.tabs);

const selectAllTabs = createSelector(selectTabs, (tabs) => tabsAdapterSelectors.selectAll(tabs));

export const selectTabIds = createSelector(selectTabs, tabsAdapterSelectors.selectIds);

//  █████╗  ██████╗████████╗██╗██╗   ██╗███████╗    ████████╗ █████╗ ██████╗
// ██╔══██╗██╔════╝╚══██╔══╝██║██║   ██║██╔════╝    ╚══██╔══╝██╔══██╗██╔══██╗
// ███████║██║        ██║   ██║██║   ██║█████╗         ██║   ███████║██████╔╝
// ██╔══██║██║        ██║   ██║╚██╗ ██╔╝██╔══╝         ██║   ██╔══██║██╔══██╗
// ██║  ██║╚██████╗   ██║   ██║ ╚████╔╝ ███████╗       ██║   ██║  ██║██████╔╝
// ╚═╝  ╚═╝ ╚═════╝   ╚═╝   ╚═╝  ╚═══╝  ╚══════╝       ╚═╝   ╚═╝  ╚═╝╚═════╝

export const selectActiveTabClientId = createSelector(
  selectActiveTabId,
  selectAllTabs,
  (activeTabId, allTabs) => allTabs.find((tab) => tab.id === activeTabId)?.clientId,
);

export const selectIsActiveTabShared = createSelector(selectTabsState, selectActiveTabId, (state, activeTabId) => {
  const entities = state.tabs.entities;

  return entities[activeTabId]?.isShared ?? false;
});

export const selectIsTabActive = createSelector(
  selectActiveTabId,
  (_state: AppState, tabId: string) => tabId,
  (activeTabId, tabId) => tabId === activeTabId,
);

export const selectActiveTabOwner = createSelector(
  selectTabsState,
  selectActiveTabId,
  (state, activeTabId) => state.tabs.entities[activeTabId]?.ownerEmail,
);

//  █████╗ ███╗   ██╗██╗   ██╗    ████████╗ █████╗ ██████╗
// ██╔══██╗████╗  ██║╚██╗ ██╔╝    ╚══██╔══╝██╔══██╗██╔══██╗
// ███████║██╔██╗ ██║ ╚████╔╝        ██║   ███████║██████╔╝
// ██╔══██║██║╚██╗██║  ╚██╔╝         ██║   ██╔══██║██╔══██╗
// ██║  ██║██║ ╚████║   ██║          ██║   ██║  ██║██████╔╝
// ╚═╝  ╚═╝╚═╝  ╚═══╝   ╚═╝          ╚═╝   ╚═╝  ╚═╝╚═════╝

export const selectTabClientId = createSelector(
  selectTabs,
  (_state: AppState, tabId: string) => tabId,
  (tabs, tabId) => tabsAdapterSelectors.selectById(tabs, tabId)?.clientId ?? undefined,
);

export const selectTabName = createSelector(
  selectTabs,
  (_state: AppState, tabId: string) => tabId,
  (tabs, tabId) => tabsAdapterSelectors.selectById(tabs, tabId)?.tabName ?? '',
);

export const selectCanDeleteTab = createSelector(selectTabs, (tabs) => tabs.ids.length > 1);

// ███████╗██╗  ██╗ █████╗ ██████╗ ███████╗██████╗     ████████╗ █████╗ ██████╗
// ██╔════╝██║  ██║██╔══██╗██╔══██╗██╔════╝██╔══██╗    ╚══██╔══╝██╔══██╗██╔══██╗
// ███████╗███████║███████║██████╔╝█████╗  ██║  ██║       ██║   ███████║██████╔╝
// ╚════██║██╔══██║██╔══██║██╔══██╗██╔══╝  ██║  ██║       ██║   ██╔══██║██╔══██╗
// ███████║██║  ██║██║  ██║██║  ██║███████╗██████╔╝       ██║   ██║  ██║██████╔╝
// ╚══════╝╚═╝  ╚═╝╚═╝  ╚═╝╚═╝  ╚═╝╚══════╝╚═════╝        ╚═╝   ╚═╝  ╚═╝╚═════╝

export const selectActiveTabSharedWithUsers = createSelector(selectActiveTab, (tab) => tab?.sharedWithUsers ?? []);

export const selectTabSharedWithUsers = createSelector(
  selectTabs,
  (_state: AppState, tabId: string) => tabId,
  (tabs, tabId) => tabsAdapterSelectors.selectById(tabs, tabId)?.sharedWithUsers ?? [],
);

export const selectSharedTabOwner = createSelector(selectActiveTab, (tab) => tab?.ownerEmail);

export const selectIsTabShared = createSelector(
  selectTabs,
  (_state: AppState, tabId: string) => tabId,
  (tabs, tabId) => tabsAdapterSelectors.selectById(tabs, tabId)?.isShared ?? false,
);
